// src/pages/Admin/AbonnementsCards.jsx (exemple de chemin)
import React, { useEffect, useMemo, useState } from "react";
import { getTotalAchats } from "../../services/achatsServices";
import { FiRefreshCw } from "react-icons/fi";

const palette = [
  "bg-blue-600",
  "bg-indigo-600",
  "bg-fuchsia-600",
  "bg-rose-600",
  "bg-orange-600",
  "bg-emerald-600",
  "bg-cyan-600",
  "bg-amber-600",
];

const pickColor = (i) => palette[i % palette.length];

const formatEUR = (n) =>
  new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  }).format(Number(n) || 0);

export default function AbonnementsCards() {
  const [stats, setStats] = useState({ total: 0, detail: {} });
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [updatedAt, setUpdatedAt] = useState(null);

  const load = async () => {
    try {
      setErr("");
      setLoading(true);
      const res = await getTotalAchats(); // attendu: { total:number, detail:{ [formule]: number } }
      setStats({
        total: res?.total ?? 0,
        detail: res?.detail ?? {},
      });
      setUpdatedAt(new Date());
    } catch (e) {
      console.error(e);
      setErr("Erreur lors du chargement des abonnements.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const entries = useMemo(
    () => Object.entries(stats.detail || {}),
    [stats.detail]
  );

  return (
    <section className="w-full font-montserrat">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-lg font-light uppercase">
          Abonnements / Formules
        </h3>
        <div className="flex items-center gap-2 text-sm opacity-80">
          {updatedAt && (
            <span>
              MAJ&nbsp;:&nbsp;
              {updatedAt.toLocaleString("fr-FR", {
                day: "2-digit",
                month: "2-digit",
                year: "2-digit",
                hour: "2-digit",
                minute: "2-digit",
              })}
            </span>
          )}
          <button
            type="button"
            onClick={load}
            className="btn btn-sm btn-outline"
            title="Recharger"
            aria-label="Recharger"
          >
            <FiRefreshCw />
          </button>
        </div>
      </div>

      {loading ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <div
              key={i}
              className="rounded-lg p-4 bg-gray-100 dark:bg-gray-800 animate-pulse h-24"
            />
          ))}
        </div>
      ) : err ? (
        <div className="p-4 rounded-lg bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300">
          {err}
        </div>
      ) : (
        <>
          {/* Carte Total */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 mb-4">
            <div className="rounded-lg shadow-lg p-4 text-white bg-gradient-to-r from-blue-600 via-fuchsia-600 to-rose-600">
              <h2 className="font-bold">Total Abonnements</h2>
              <p className="text-3xl font-semibold mt-2">
                {formatEUR(stats.total)}
              </p>
            </div>

            {/* Cartes par formule */}
            {entries.length === 0 ? (
              <div className="col-span-1 sm:col-span-2 lg:col-span-3 rounded-lg border border-dashed p-4 text-sm opacity-70 dark:border-gray-600">
                Aucune formule enregistrée.
              </div>
            ) : (
              entries.map(([formule, montant], i) => (
                <div
                  key={formule}
                  className={`rounded-lg shadow-lg p-4 text-white ${pickColor(
                    i
                  )}`}
                >
                  <h2 className="font-bold truncate">{formule}</h2>
                  <p className="text-3xl font-semibold mt-2">
                    {formatEUR(montant)}
                  </p>
                </div>
              ))
            )}
          </div>
        </>
      )}
    </section>
  );
}
